import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Sparkles, LayoutDashboard, LogOut, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/roadmaps", label: "Career Hubs" },
  { to: "/ebooks", label: "Ebooks" },
  { to: "/playbooks", label: "Playbooks" },
  { to: "/career-ai", label: "Career AI" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, openAuth, logout } = useAuth();

  useEffect(() => {
    setOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  const initials = user?.full_name
    ? user.full_name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : (user?.email || "U").slice(0, 1).toUpperCase();

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="w-8 h-8 rounded-xl bg-[#0A2540] text-white grid place-items-center transition-transform duration-300 group-hover:rotate-6">
            <img
              src="/vs-monogram.png"
              alt=""
              aria-hidden="true"
              className="w-5 h-5 object-contain"
            />
          </div>
          <span className="font-display text-lg font-semibold text-[#0A2540] tracking-tight">
            VantStride
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "text-[#0A2540] bg-slate-100"
                    : "text-slate-600 hover:text-[#0A2540] hover:bg-slate-50"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setProfileOpen((v) => !v)}
                data-testid="nav-profile-btn"
                className="inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border border-slate-200 hover:border-[#0A2540]/25 hover:bg-slate-50 transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <span className="w-7 h-7 rounded-full bg-[#0A2540] text-white grid place-items-center text-[11px] font-semibold">
                    {initials}
                  </span>
                )}
                <span className="text-xs font-medium text-slate-700 max-w-[140px] truncate">
                  {user.full_name || user.email}
                </span>
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-white border border-slate-100 shadow-[0_20px_60px_rgba(10,37,64,0.12)] p-1.5 z-40">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Signed in as</div>
                    <div className="text-sm font-medium text-[#0A2540] truncate">{user.email}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setProfileOpen(false); navigate("/dashboard"); }}
                    data-testid="nav-dashboard-link"
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-slate-50 text-sm flex items-center gap-2 text-[#0A2540]"
                  >
                    <LayoutDashboard className="w-4 h-4" /> Dashboard
                  </button>
                  <button
                    type="button"
                    onClick={async () => { setProfileOpen(false); await logout(); navigate("/"); }}
                    data-testid="nav-signout-btn"
                    className="w-full text-left px-3 py-2 rounded-xl hover:bg-slate-50 text-sm flex items-center gap-2 text-slate-600"
                  >
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => openAuth({ tab: "login" })}
                data-testid="nav-signin-btn"
                className="text-sm font-medium text-slate-700 hover:text-[#0A2540] px-3 py-2 rounded-full transition-colors"
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => openAuth({ tab: "signup" })}
                data-testid="nav-create-account-btn"
                className="inline-flex items-center gap-2 bg-[#0A2540] text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-[#051A2E] transition-colors active:scale-95"
              >
                Create Free Account
              </button>
            </>
          )}
        </div>

        <button
          type="button"
          className="lg:hidden p-2 rounded-full hover:bg-slate-100 transition-colors"
          onClick={() => setOpen((s) => !s)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-sm font-medium ${
                    isActive ? "bg-slate-100 text-[#0A2540]" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" className="mt-2 px-4 py-3 rounded-xl bg-slate-100 text-[#0A2540] text-sm font-medium">
                  Dashboard
                </Link>
                <button
                  type="button"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="px-4 py-3 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 text-left"
                >
                  Sign out
                </button>
              </>
            ) : (
              <div className="mt-2 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => openAuth({ tab: "login" })}
                  className="w-full text-center border border-slate-200 rounded-full px-5 py-3 text-sm font-medium"
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => openAuth({ tab: "signup" })}
                  className="w-full bg-[#0A2540] text-white text-center rounded-full px-5 py-3 text-sm font-semibold"
                >
                  Create Free Account
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
